/**
 * OddsChartsWidget — Odds movement charts organized by market type
 * - Sub-tabs: Moneyline | Spreads | Totals
 * - Fetches chart posts from #mlb-moneylines, #mlb-spreads, #mlb-totals
 * - Displays chart images in a responsive grid
 * - Charts are generated PNGs with team names, odds lines, and bookmaker logos
 * Refresh: 60s | CSS prefix: ocw-
 */

import { useState, useEffect, useCallback } from 'react'
import { fetchFromProxy, imageUrl } from '../lib/api'
import { RefreshCw, BarChart3, AlertCircle } from 'lucide-react'
import './OddsChartsWidget.css'

const MARKET_TABS = [
  { id: 'moneyline', label: 'Moneyline', channel: '#mlb-moneylines' },
  { id: 'spreads', label: 'Spreads', channel: '#mlb-spreads' },
  { id: 'totals', label: 'Totals', channel: '#mlb-totals' },
]

function timeAgo(isoStr) {
  const diff = Date.now() - new Date(isoStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

export default function OddsChartsWidget() {
  const [activeMarket, setActiveMarket] = useState('moneyline')
  const [chartsByMarket, setChartsByMarket] = useState({
    moneyline: [],
    spreads: [],
    totals: [],
  })
  const [summaryByMarket, setSummaryByMarket] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [selectedChart, setSelectedChart] = useState(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Fetch all three channels in parallel
      const results = await Promise.all(
        MARKET_TABS.map(tab =>
          fetchFromProxy('/posts', { channel: tab.channel, limit: '100' })
        )
      )

      const newCharts = {}
      const newSummary = {}

      MARKET_TABS.forEach((tab, i) => {
        const res = results[i]
        if (res.status !== 'ok' || !res.data) {
          newCharts[tab.id] = []
          return
        }

        const posts = res.data.posts || (Array.isArray(res.data) ? res.data : [])

        // Separate chart posts from summary posts
        const charts = posts.filter(p => p.post_type === 'chart' && p.image_url)
        const summary = posts.find(p => p.post_type === 'text_embed' && p.description?.includes('Odds Update Complete'))

        newCharts[tab.id] = charts
        if (summary) {
          newSummary[tab.id] = summary
        }
      })

      setChartsByMarket(newCharts)
      setSummaryByMarket(newSummary)
      setLastUpdated(new Date())
    } catch (e) {
      setError('Failed to load odds charts')
    }

    setLoading(false)
  }, [])

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, 60000)
    return () => clearInterval(interval)
  }, [fetchData])

  const activeCharts = chartsByMarket[activeMarket] || []
  const activeSummary = summaryByMarket.moneyline // Summary is always in moneylines channel

  // Parse last update time from summary
  const lastOddsUpdate = activeSummary?.timestamp
    ? timeAgo(activeSummary.timestamp)
    : null
  const totalGames = activeSummary?.description?.match(/\*\*(\d+) Games\*\*/)?.[1] || null

  return (
    <div className="ocw-widget">
      <div className="ocw-header">
        <div className="ocw-header-left">
          <BarChart3 size={18} className="ocw-header-icon" />
          <h2 className="ocw-title">Odds Movement</h2>
          {totalGames && (
            <span className="ocw-count">{totalGames} games</span>
          )}
        </div>
        <div className="ocw-header-right">
          {lastOddsUpdate && (
            <span className="ocw-last-updated">Updated {lastOddsUpdate}</span>
          )}
          <button className="ocw-refresh" onClick={fetchData} title="Refresh">
            <RefreshCw size={14} className={loading ? 'ocw-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Market sub-tabs */}
      <div className="ocw-tabs">
        {MARKET_TABS.map(tab => (
          <button
            key={tab.id}
            className={`ocw-tab ${activeMarket === tab.id ? 'active' : ''}`}
            onClick={() => { setActiveMarket(tab.id); setSelectedChart(null) }}
          >
            <span className="ocw-tab-label">{tab.label}</span>
            <span className="ocw-tab-count">{(chartsByMarket[tab.id] || []).length}</span>
          </button>
        ))}
      </div>

      {error && (
        <div className="ocw-error">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}

      {loading && activeCharts.length === 0 ? (
        <div className="ocw-loading">Loading odds charts...</div>
      ) : (
        <div className="ocw-body">
          {activeCharts.length === 0 && !loading && (
            <div className="ocw-empty">
              No {activeMarket} charts available. Charts appear after the odds monitor completes a cycle.
            </div>
          )}

          {/* Expanded chart view */}
          {selectedChart && (
            <div className="ocw-expanded" onClick={() => setSelectedChart(null)}>
              <div className="ocw-expanded-inner" onClick={e => e.stopPropagation()}>
                <button className="ocw-expanded-close" onClick={() => setSelectedChart(null)}>✕</button>
                <img
                  src={imageUrl(selectedChart.image_url)}
                  alt="Odds chart"
                  className="ocw-expanded-img"
                />
                <div className="ocw-expanded-meta">
                  <span>{timeAgo(selectedChart.timestamp)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Chart grid */}
          <div className="ocw-grid">
            {activeCharts.map((chart, i) => (
              <div
                key={`${chart.timestamp}-${i}`}
                className="ocw-chart-card"
                onClick={() => setSelectedChart(chart)}
              >
                <img
                  src={imageUrl(chart.image_url)}
                  alt="Odds movement chart"
                  className="ocw-chart-img"
                  loading="lazy"
                  onError={(e) => { e.target.closest('.ocw-chart-card').style.display = 'none' }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
